import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Select,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconCircleCheck, IconSend } from '@tabler/icons-react'
import { DatePickerInput } from '@mantine/dates'
import { formatDate, REQUEST_RECIPIENT } from './intake'
import {
  buildSandboxOutlookUrl,
  SANDBOX_INITIAL,
  type SandboxProvisioningValues,
} from './sandboxProvisioning'

const required = (message: string) => (v: string) =>
  String(v ?? '').trim().length === 0 ? message : null

export function SandboxProvisioningForm() {
  const [submitted, setSubmitted] = useState(false)

  const form = useForm<SandboxProvisioningValues>({
    initialValues: SANDBOX_INITIAL,
    validate: {
      sandboxName: required('Sandbox Name is required'),
      requestor: required('Requestor is required'),
      businessTeam: required('Business Team is required'),
      purpose: required('Purpose of Sandbox is required'),
      usersRequiringAccess: required('Users Requiring Access is required'),
      dataAssetsRequired: required('Data Assets Required is required'),
      requiredByDate: required('Required By Date is required'),
      approvalObtained: required('Approval Obtained is required'),
    },
  })

  const handleSubmit = form.onSubmit((values) => {
    const loginHint = localStorage.getItem('prism_username')
    window.open(
      buildSandboxOutlookUrl(values, loginHint),
      '_blank',
      'noopener,noreferrer'
    )
    setSubmitted(true)
  })

  return (
    <Box>
      <Text size="sm" c="gray.6" mb="md">
        Request creation of a new sandbox environment or access to specific data
        assets for development, testing, or exploration purposes. Submitting
        opens an Outlook email to {REQUEST_RECIPIENT}.
      </Text>

      {submitted && (
        <Alert
          color="green"
          icon={<IconCircleCheck size={16} />}
          title="Email drafted in Outlook"
          mb="md"
          withCloseButton
          onClose={() => setSubmitted(false)}
        >
          A &quot;Prism Portal Request - Sandbox Provisioning&quot; email to{' '}
          {REQUEST_RECIPIENT} should have opened in an Outlook compose tab.
          Review and send it to complete your request.
        </Alert>
      )}

      <Card shadow="sm" withBorder>
        <Box p="md">
          <form onSubmit={handleSubmit}>
            <Group grow mb="md">
              <TextInput
                label="Sandbox Name (Required)"
                placeholder="Name for the sandbox"
                {...form.getInputProps('sandboxName')}
              />
              <TextInput
                label="Requestor (Required)"
                placeholder="Your name"
                {...form.getInputProps('requestor')}
              />
            </Group>

            <Group grow mb="md">
              <TextInput
                label="Business Team (Required)"
                placeholder="e.g. Marketing Analytics"
                {...form.getInputProps('businessTeam')}
              />
              <Select
                label="Duration"
                placeholder="Select"
                data={['30 Days', '60 Days', '90 Days']}
                {...form.getInputProps('duration')}
              />
            </Group>

            <Textarea
              label="Purpose of Sandbox (Required)"
              placeholder="What will this sandbox be used for?"
              minRows={2}
              autosize
              mb="md"
              {...form.getInputProps('purpose')}
            />

            <TextInput
              label="Users Requiring Access (Required)"
              placeholder="Comma-separated names or emails"
              mb="md"
              {...form.getInputProps('usersRequiringAccess')}
            />

            <Textarea
              label="Data Assets Required (Required)"
              placeholder="List tables, catalogs, schemas, or data products"
              minRows={2}
              autosize
              mb="md"
              {...form.getInputProps('dataAssetsRequired')}
            />

            <Group grow mb="md">
              <TextInput
                label="Expected Data Volume"
                placeholder="e.g. ~500GB"
                {...form.getInputProps('expectedDataVolume')}
              />
              <Select
                label="Security Classification"
                placeholder="Select"
                data={['Public', 'Internal', 'Confidential']}
                {...form.getInputProps('securityClassification')}
              />
            </Group>

            <Group grow mb="md">
              <DatePickerInput
                label="Required By Date (Required)"
                placeholder="Pick a date"
                valueFormat="M/D/YYYY"
                value={form.values.requiredByDate ? new Date(form.values.requiredByDate) : new Date()}
                onChange={(d) => form.setFieldValue('requiredByDate', d ? formatDate(d) : '')}
                error={form.errors.requiredByDate}
              />
              <TextInput
                label="Approval Obtained (Required)"
                placeholder="Manager, Data Owner"
                {...form.getInputProps('approvalObtained')}
              />
            </Group>

            <Divider my="md" />
            <Group justify="flex-end">
              <Button
                variant="light"
                color="gray"
                onClick={() => {
                  form.reset()
                  setSubmitted(false)
                }}
              >
                Clear
              </Button>
              <Button
                type="submit"
                color="prism"
                leftSection={<IconSend size={16} />}
              >
                Submit Request
              </Button>
            </Group>
          </form>
        </Box>
      </Card>
    </Box>
  )
}
